import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import { Address } from 'viem';

import { LaunchpadSalePoolType } from '@api';

import { wagmiConfig, WagmiConfigChain } from '@shared/common/providers/Web3Provider/wagmi';
import {
  DEFAULT_TARGET_CHAIN,
  LAST_FCFS_CONTRACT_VERSION,
  LAST_PRO_RATA_CONTRACT_VERSION,
  LAUNCHPAD_DEPOSIT_CONTRACTS,
} from '@shared/constants';
import formatUnits from '@shared/helpers/web3/formatters/formatUnits';

import { isProRataType } from '@launchpad/utils/contract';

import useDepositTokenQuery from './useDepositTokenQuery';

type Props = {
  depositContractAddress?: Address;
  chainId?: WagmiConfigChain['id'];
  isProRata?: boolean;
};

export const getTotalDeposited = async ({
  depositContractAddress,
  chainId = DEFAULT_TARGET_CHAIN.id,
  isProRata = false,
}: Props) => {
  if (isProRata) {
    const totalDeposited = await readContract(wagmiConfig, {
      ...LAUNCHPAD_DEPOSIT_CONTRACTS.PRO_RATA[LAST_PRO_RATA_CONTRACT_VERSION],
      chainId,
      address: depositContractAddress as Address,
      functionName: 'totalBaseDeposited',
    });
    return totalDeposited;
  }

  const totalDeposited = await readContract(wagmiConfig, {
    ...LAUNCHPAD_DEPOSIT_CONTRACTS.FCFS[LAST_FCFS_CONTRACT_VERSION],
    chainId,
    address: depositContractAddress as Address,
    functionName: 'totalDeposited',
  });

  return totalDeposited;
};

export const useLaunchpadTotalDeposited = ({
  depositContractAddress,
  type,
  enabled,
}: {
  depositContractAddress?: string | null;
  type?: LaunchpadSalePoolType;
  enabled?: boolean;
}) => {
  const { data: depositTokenInfo } = useDepositTokenQuery({
    depositContractAddress,
    type,
  });

  return useQuery({
    queryFn: () =>
      getTotalDeposited({
        isProRata: isProRataType(type),
        depositContractAddress: depositContractAddress as Address,
      }),
    select: (data) => (data ? Number(formatUnits(data, depositTokenInfo?.decimals)) : 0),
    queryKey: ['poolTotalDeposited', depositTokenInfo?.decimals, depositContractAddress, type],
    staleTime: 0,
    enabled: !!depositContractAddress && !!depositTokenInfo && enabled,
  });
};

export const useRefetchAllLaunchpadTotalDepositedMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.refetchQueries({
        queryKey: ['poolTotalDeposited'],
      }),
  });
};
